import { JWTPayload } from '../types';

/**
 * Decode JWT token without verification (client-side only)
 */
function decodeToken(token: string): JWTPayload | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

class AuthService {
  /**
   * Login with Google OAuth
   */
  loginWithGoogle(): void {
    // The server redirects back to our callback page with tokens
    // Note: The returnUrl should be the full URL including the path
    // The React Router expects /auth/callback (not /admin/auth/callback)
    const returnUrl = encodeURIComponent(`${window.location.origin}/auth/callback`);
    // Static admin host has no /api — OAuth must hit the real API (VITE_SERVER_URL in prod).
    const server = (import.meta.env.VITE_SERVER_URL || '').replace(/\/$/, '');
    const apiBase = server ? `${server}/api/v1` : '/api/v1';
    const googleAuthUrl = `${apiBase}/auth/google?return=${returnUrl}&client=admin`;
    window.location.href = googleAuthUrl;
  }

  /**
   * Get JWT token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem('adminToken');
  }

  /**
   * Set JWT token in localStorage
   */
  setToken(token: string): void {
    localStorage.setItem('adminToken', token);
  }

  /**
   * Remove token from localStorage
   */
  logout(): void {
    localStorage.removeItem('adminToken');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const payload = decodeToken(token);
    if (!payload) return false;

    // Check if token is expired
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) {
      this.logout();
      return false;
    }

    return true;
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const payload = decodeToken(token);
    if (!payload) return false;

    return payload.role === 'admin' || payload.role === 'analyst';
  }

  /**
   * Get user role from token
   */
  getRole(): 'analyst' | 'admin' | null {
    const token = this.getToken();
    if (!token) return null;

    const payload = decodeToken(token);
    if (!payload) return null;

    if (payload.role === 'admin' || payload.role === 'analyst') {
      return payload.role as 'analyst' | 'admin';
    }

    return null;
  }

  /**
   * Get user ID from token
   */
  getUserId(): string | null {
    const token = this.getToken();
    if (!token) return null;

    const payload = decodeToken(token);
    return payload?.userId || null;
  }

  /**
   * Get user email from token
   */
  getEmail(): string | null {
    const token = this.getToken();
    if (!token) return null;

    const payload = decodeToken(token);
    return payload?.email || null;
  }
}

export const authService = new AuthService();
export default authService;

